import { PropsWithChildren, ReactNode, useContext } from "react"
import {
  Dialog,
  ModalOverlay,
  ModalOverlayProps,
  OverlayTriggerStateContext,
  Modal as RACModal,
} from "react-aria-components"
import { useTranslation } from "react-i18next"
import { OverlayTriggerState } from "react-stately"

import { Button } from "@/globalComponents/atoms/Button"
import { cn } from "@/utilities/etc"

import Icon from "../atoms/Icon"

import styles from "./Modal.module.css"

export interface ModalProps extends ModalOverlayProps {
  modalStyle?: "centered" | "drawer" // Default is centered
}

const Modal = (props: PropsWithChildren<ModalProps>) => {
  return (
    <ModalOverlay className={styles.overlay} {...props}>
      <RACModal
        className={
          props.modalStyle === "drawer"
            ? styles.drawerModal
            : styles.centeredModal
        }
      >
        <Dialog className={styles.dialog}>{props.children}</Dialog>
      </RACModal>
    </ModalOverlay>
  )
}

interface ModalHeaderProps {
  hideClose?: boolean
  className?: string
}

Modal.Header = (props: PropsWithChildren<ModalHeaderProps>) => {
  const context = useContext(OverlayTriggerStateContext)
  const { t } = useTranslation()
  return (
    <div className={cn(styles.modalHeader, props.className)}>
      {props.children}
      {!props.hideClose && (
        <Button
          hue="neutral"
          presentation="ghost"
          aria-label={t("common.close")}
          onPress={context?.close}
          style={{ width: "fit-content" }}
        >
          <Icon name="close" />
        </Button>
      )}
    </div>
  )
}

Modal.Body = (props: PropsWithChildren) => {
  return <div className={styles.modalBody}>{props.children}</div>
}

interface ModalFooterProps {
  arrangement?: "vertical" | "horizontal"
  children: (opts: OverlayTriggerState) => ReactNode
}

Modal.Footer = (props: ModalFooterProps) => {
  const context = useContext(OverlayTriggerStateContext)
  if (!context) {
    console.error("Modal.Footer must be used in a Modal")
    return <></>
  }
  return (
    <div
      className={styles.modalFooter}
      data-arrangement={props.arrangement || "horizontal"}
    >
      {props.children(context)}
    </div>
  )
}

export default Modal
